import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import PetRenderer from '../components/PetRenderer';
import { ViewModeToggle } from '../components/visuals';
import EmotePlayer from '../components/EmotePlayer';
import FoodPouch from '../components/FoodPouch';
import { EMOTE_CATALOG, EMOTE_CATEGORIES } from '../components/cosmetics';
import { getUnlocks, purchaseItem } from '../api/client';
import {
  HeartIcon, FoodIcon, EnergyIcon, StarIcon,
  CosmeticsIcon, ChatIcon, PersonalityIcon, ArrowIcon,
} from '../components/icons';
import './PetView.css';

export default function PetView() {
  const { slot } = useParams();
  const slotNum = parseInt(slot, 10);
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { pets, releasePet, refreshData } = usePets();
  const [confirmRelease, setConfirmRelease] = useState(false);
  const [actionFeedback, setActionFeedback] = useState('');
  const [showFoodPouch, setShowFoodPouch] = useState(false);
  const [showEmotes, setShowEmotes] = useState(false);
  const [activeEmote, setActiveEmote] = useState(null);
  const [emoteCategory, setEmoteCategory] = useState('Greetings');
  const [ownedEmotes, setOwnedEmotes] = useState(new Set());
  const [purchasingEmote, setPurchasingEmote] = useState(null);

  React.useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch owned emotes
  useEffect(() => {
    if (!isAuthenticated) return;
    getUnlocks().then(res => {
      const ids = (res.data?.unlocks || []).map(u =>
        u.startsWith('unlock_') ? u.replace('unlock_', '') : u
      );
      setOwnedEmotes(new Set(ids));
    }).catch(() => {});
  }, [isAuthenticated]);

  const isEmoteOwned = (emote) => emote.price === 0 || ownedEmotes.has(emote.id);

  const handlePlayEmote = (emote) => {
    if (!isEmoteOwned(emote)) {
      handleBuyEmote(emote);
      return;
    }
    setActiveEmote(emote.id);
  };

  const handleBuyEmote = async (emote) => {
    setPurchasingEmote(emote.id);
    try {
      const res = await purchaseItem(emote.id);
      if (res.data?.success) {
        setOwnedEmotes(prev => new Set([...prev, emote.id]));
        showFeedback(`Purchased ${emote.name}!`);
      } else {
        const msg = res.data?.message || '';
        if (msg.toLowerCase().includes('not enough treats')) {
          window.open('/shop', '_blank');
        } else {
          showFeedback(msg || 'Purchase failed');
        }
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 404 || (err.response?.data?.detail || '').includes('not enough')) {
        window.open('/shop', '_blank');
      } else {
        showFeedback('Purchase failed');
      }
    }
    setPurchasingEmote(null);
  };

  const pet = pets.find((p) => p.slot_index === slotNum);

  if (authLoading) return <div className="petview-loading">Loading...</div>;
  if (!pet) {
    return (
      <div className="petview-empty">
        <p>No pet in this slot.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/pets')}>Back to Kennel</button>
      </div>
    );
  }

  const showFeedback = (msg) => {
    setActionFeedback(msg);
    setTimeout(() => setActionFeedback(''), 1500);
  };

  const handleAction = async (action) => {
    try {
      const { interact } = await import('../api/client');
      const res = await interact(action, slotNum);
      if (res.data.on_cooldown) {
        const mins = Math.floor(res.data.cooldown_remaining / 60);
        const secs = res.data.cooldown_remaining % 60;
        showFeedback(`Cooldown: ${mins}m ${secs}s remaining`);
      } else {
        showFeedback(res.data.message);
        refreshData(); // pull updated stats
      }
    } catch (err) {
      showFeedback(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleFeed = () => handleAction('feed');
  const handlePlay = () => handleAction('play');
  const handleRest = () => handleAction('rest');
  const handlePet = () => handleAction('pet');

  const handleRelease = async () => {
    await releasePet(slotNum);
    navigate('/pets');
  };

  const xpForNextLevel = pet.level * 100;
  const xpInLevel = pet.xp % 100;
  const xpPercent = (xpInLevel / 100) * 100;

  return (
    <div className="petview-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate('/pets')}>
          <ArrowIcon size={18} direction="left" /> Back to Kennel
        </button>
        <div className="petview-view-toggle"><ViewModeToggle compact /></div>

        <div className="petview-layout">
          {/* Left: Pet renderer */}
          <div className="petview-visual">
            <EmotePlayer emoteId={activeEmote} onComplete={() => setActiveEmote(null)}>
              <PetRenderer
                petType={pet.pet_type_id}
                variant={pet.variant_id}
                cosmetics={pet.equipped_cosmetics}
                cosmeticPositions={pet.cosmetic_positions || pet.cosmeticPositions || {}}
                mood={pet.mood}
                size={280}
                interactive={!activeEmote}
              />
            </EmotePlayer>

            {actionFeedback && (
              <div className="action-feedback">{actionFeedback}</div>
            )}
          </div>

          {/* Right: Info & actions */}
          <div className="petview-info">
            <div className="petview-name-row">
              <h1>{pet.pet_name}</h1>
              <span className={`kennel-mood mood-${pet.mood}`}>{pet.mood}</span>
            </div>

            <p className="petview-type">
              {pet.pet_type_id.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
              {pet.variant_id && ` (${pet.variant_id})`}
            </p>

            {/* Level & XP */}
            <div className="petview-xp-section">
              <div className="petview-level">
                <StarIcon size={20} color="var(--yellow-warm)" />
                <span>Level {pet.level}</span>
              </div>
              <div className="stat-bar-wrapper">
                <div className="stat-bar">
                  <div className="stat-bar-fill stat-bar-xp" style={{ width: `${xpPercent}%` }} />
                </div>
                <span className="stat-bar-label">{xpInLevel}/{xpForNextLevel > 0 ? 100 : 100} XP</span>
              </div>
            </div>

            {/* Need bars */}
            <div className="petview-needs">
              <div className="need-row">
                <FoodIcon size={18} color="#FF8A65" />
                <span className="need-label">Hunger</span>
                <div className="stat-bar">
                  <div
                    className="stat-bar-fill stat-bar-hunger"
                    style={{ width: `${pet.hunger * 100}%` }}
                  />
                </div>
                <span className="need-value">{Math.round(pet.hunger * 100)}%</span>
              </div>
              <div className="need-row">
                <HeartIcon size={18} color="#E57373" />
                <span className="need-label">Happiness</span>
                <div className="stat-bar">
                  <div
                    className="stat-bar-fill stat-bar-happiness"
                    style={{ width: `${pet.happiness * 100}%` }}
                  />
                </div>
                <span className="need-value">{Math.round(pet.happiness * 100)}%</span>
              </div>
              <div className="need-row">
                <EnergyIcon size={18} color="var(--orange-primary)" />
                <span className="need-label">Energy</span>
                <div className="stat-bar">
                  <div
                    className="stat-bar-fill stat-bar-energy"
                    style={{ width: `${pet.energy * 100}%` }}
                  />
                </div>
                <span className="need-value">{Math.round(pet.energy * 100)}%</span>
              </div>
            </div>

            {/* Actions */}
            <div className="petview-actions">
              <button className="btn btn-primary action-btn" onClick={() => setShowFoodPouch(!showFoodPouch)}>
                <FoodIcon size={18} color="white" /> {showFoodPouch ? 'Close' : 'Feed'}
              </button>
              <button className="btn btn-secondary action-btn" onClick={handlePlay}>
                <HeartIcon size={18} color="white" /> Play
              </button>
              <button className="btn btn-ghost action-btn" onClick={handleRest}>
                <EnergyIcon size={18} /> Rest
              </button>
              <button className="btn btn-ghost action-btn" onClick={handlePet}>
                <HeartIcon size={18} /> Pet
              </button>
            </div>

            {/* Food Pouch (shown when Feed is clicked) */}
            {showFoodPouch && (
              <FoodPouch
                petSlot={slotNum}
                petType={pet.pet_type_id}
                onClose={() => setShowFoodPouch(false)}
              />
            )}

            {/* Traits */}
            {pet.trait_ids && pet.trait_ids.length > 0 && (
              <div className="petview-traits">
                <h3><PersonalityIcon size={18} /> Traits</h3>
                <div className="trait-chips">
                  {pet.trait_ids.map((trait) => (
                    <span key={trait} className="trait-chip">
                      {trait.replace(/\b\w/g, (c) => c.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="petview-nav-actions">
              <button
                className="btn btn-ghost"
                onClick={() => navigate(`/pets/${slotNum}/cosmetics`)}
              >
                <CosmeticsIcon size={18} /> Dress Up
              </button>
              <button
                className={`btn ${showEmotes ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setShowEmotes(!showEmotes)}
              >
                🎭 Emotes
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => navigate(`/pets/${slotNum}/chat`)}
              >
                <ChatIcon size={18} /> Chat
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => navigate(`/pets/${slotNum}/abilities`)}
              >
                ⭐ Abilities
              </button>
            </div>

            {/* Emotes Panel */}
            {showEmotes && (
              <div className="petview-emotes">
                <div className="emote-category-tabs">
                  {EMOTE_CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      className={`emote-tab ${emoteCategory === cat ? 'active' : ''}`}
                      onClick={() => setEmoteCategory(cat)}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="emote-grid">
                  {EMOTE_CATALOG.filter(e => e.category === emoteCategory).map(emote => {
                    const owned = isEmoteOwned(emote);
                    const isPlaying = activeEmote === emote.id;
                    const isPurchasing = purchasingEmote === emote.id;
                    return (
                      <button
                        key={emote.id}
                        className={`emote-card ${owned ? '' : 'locked'} ${isPlaying ? 'playing' : ''}`}
                        onClick={() => handlePlayEmote(emote)}
                        disabled={isPurchasing || isPlaying}
                        title={emote.description}
                      >
                        <span className="emote-card-name">{emote.name}</span>
                        {isPurchasing ? (
                          <span className="emote-card-price">...</span>
                        ) : !owned ? (
                          <span className="emote-card-price">🦴 {emote.price}</span>
                        ) : isPlaying ? (
                          <span className="emote-card-status">Playing</span>
                        ) : (
                          <span className="emote-card-status">Play</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Release */}
            <div className="petview-release">
              {!confirmRelease ? (
                <button className="release-btn" onClick={() => setConfirmRelease(true)}>
                  Release this pet
                </button>
              ) : (
                <div className="release-confirm">
                  <p>Are you sure? This cannot be undone.</p>
                  <div className="release-confirm-actions">
                    <button className="btn btn-ghost" onClick={() => setConfirmRelease(false)}>Cancel</button>
                    <button className="btn release-confirm-btn" onClick={handleRelease}>Release</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
